/**
 * Structured Data Schema Generation Module
 * Handles Schema.org structured data for SEO
 * Part of SEO optimization - follows AIM atomicity principle
 */

import { generateCanonicalUrl } from './meta'

/**
 * Generate LocalBusiness structured data for entities
 */
export const generateLocalBusinessSchema = (entity) => {
  if (!entity) return null

  const baseUrl = import.meta.env.VITE_SITE_URL || 'http://localhost:3001'
  const entityUrl = generateCanonicalUrl(`/entities/${entity.id}`)
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${entityUrl}#business`,
    name: entity.name,
    description: entity.description || `${entity.name} - Mobile ${entity.category || 'vendor'}`,
    url: entityUrl,
  }

  // Add contact information if available
  if (entity.phone) {
    schema.telephone = entity.phone
  }
  if (entity.email) {
    schema.email = entity.email
  }

  // Add image if available - handle both relative and absolute URLs
  if (entity.imageURL || entity.imageUrl) {
    const imageUrl = entity.imageURL || entity.imageUrl
    schema.image = {
      '@type': 'ImageObject',
      url: imageUrl.startsWith('http') ? imageUrl : `${baseUrl}${imageUrl}`,
      caption: `${entity.name} - Mobile ${entity.category || 'vendor'}`
    }
  }

  // Add website if available
  if (entity.website) {
    schema.sameAs = [entity.website]
  }

  // Add location if available (current location from latest sighting)
  if ((entity.currentLatitude || entity.current_latitude) && (entity.currentLongitude || entity.current_longitude)) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: entity.currentLatitude || entity.current_latitude,
      longitude: entity.currentLongitude || entity.current_longitude,
    }

    if (entity.currentAddress || entity.current_address) {
      schema.address = {
        '@type': 'PostalAddress',
        streetAddress: entity.currentAddress || entity.current_address,
      }
    }
  }

  // Add category and type
  if (entity.category) {
    schema.servesCuisine = entity.category
  }
  
  // Add service type based on entity type
  if (entity.type) {
    schema.additionalType = `https://schema.org/${entity.type === 'food_truck' ? 'FoodEstablishment' : 'LocalBusiness'}`
  }

  // Add price range if available
  if (entity.priceRange) {
    schema.priceRange = entity.priceRange
  }

  // Add verification badge
  if (entity.isVerified || entity.is_verified) {
    schema.award = 'Verified Vendor'
  }

  return schema
}

/**
 * Generate AggregateRating structured data
 */
export const generateAggregateRatingSchema = (entity, reviews) => {
  if (!reviews || reviews.length === 0) return null

  const totalRating = reviews.reduce((sum, review) => sum + (review.rating || 0), 0)
  const averageRating = totalRating / reviews.length

  return {
    '@type': 'AggregateRating',
    ratingValue: averageRating.toFixed(1),
    reviewCount: reviews.length,
    bestRating: '5',
    worstRating: '1',
  }
}

/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: generateCanonicalUrl(crumb.path),
    })),
  }
}

/**
 * Generate Organization structured data for homepage
 */
export const generateOrganizationSchema = () => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'http://localhost:3001'
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${baseUrl}#organization`,
    name: 'TrckIt',
    url: baseUrl,
    logo: `${baseUrl}/logo.png`,
    description: 'Global crowdsourcing platform for tracking and discovering mobile vendors like food trucks, ice cream vendors, and mobile services',
    sameAs: [
      // Add social media profiles when available
      // 'https://twitter.com/trckit',
      // 'https://facebook.com/trckit',
      // 'https://instagram.com/trckit',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      availableLanguage: ['English', 'Spanish'],
    },
  }
}

/**
 * Generate WebSite structured data with search action
 */
export const generateWebSiteSchema = () => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'http://localhost:3001'
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${baseUrl}#website`,
    url: baseUrl,
    name: 'TrckIt',
    description: 'Track and find mobile entities globally',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}

/**
 * Generate combined entity schema with business and rating
 */
export const generateEntitySchema = (entity, reviews = []) => {
  const businessSchema = generateLocalBusinessSchema(entity)
  if (!businessSchema) return null

  const ratingSchema = generateAggregateRatingSchema(entity, reviews)
  if (ratingSchema) {
    businessSchema.aggregateRating = ratingSchema
  }

  return businessSchema
}

/**
 * Generate FAQ schema for rich snippets
 * @param {Array} faqs - Array of {question, answer} objects
 */
export const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }
}

/**
 * Generate Article schema for blog posts
 * @param {Object} article - Article data with title, description, author, date, image
 */
export const generateArticleSchema = (article) => {
  if (!article) return null
  
  const baseUrl = import.meta.env.VITE_SITE_URL || 'http://localhost:3001'
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.headline || article.title,
    description: article.description,
    image: article.image || `${baseUrl}/logo.png`,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author || 'TrckIt Team'
    },
    publisher: {
      '@type': 'Organization',
      name: 'TrckIt',
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/logo.png`
      }
    }
  }
}

/**
 * Generate HowTo schema for tutorials
 * @param {Object} howTo - HowTo data with name, description, steps
 */
export const generateHowToSchema = (howTo) => {
  if (!howTo) return null
  
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: howTo.name,
    description: howTo.description,
    step: howTo.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      image: step.image
    }))
  }
}

/**
 * Generate FAQPage schema for FAQ pages
 * @param {Array} faqs - Array of FAQ objects with question and answer properties
 */
export const generateFAQPageSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null
  
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }
}

/**
 * Generate ItemList schema for city/collection pages
 * @param {Array} entities - Array of entity objects with id and name
 * @param {string} cityName - Display name of the city (e.g. "Austin, TX")
 * @param {string} baseUrl - Site base URL
 */
export const generateItemListSchema = (entities, cityName, baseUrl) => {
  if (!entities || entities.length === 0) return null
  const base = baseUrl || import.meta.env.VITE_SITE_URL || 'http://localhost:3001'
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: cityName ? `Food Trucks & Vendors in ${cityName}` : 'Vendors',
    numberOfItems: entities.length,
    itemListElement: entities.map((entity, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: entity.name,
      url: `${base}/entity/${entity.id}`,
    })),
  }
}
